import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wind, Loader2, Check, Gift } from 'lucide-react';
import { toast } from 'sonner';

const SIDE = 'https://images.pexels.com/photos/28860321/pexels-photo-28860321/free-photo-of-exciting-kiteboarding-adventure-on-ocean-waves.jpeg?auto=compress&cs=tinysrgb&w=1280';

export default function Register() {
  const { register } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get('ref') || '';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t('auth.register.passwordShort')); return; }
    setBusy(true);
    try {
      const u = await register(name, email, password, i18n.language, refCode);
      toast.success(t('auth.register.welcomeToast', { name: u.name }));
      navigate(sessionStorage.getItem('kiteiq_pending_plan') ? '/pricing' : '/onboarding');
    } catch (err) {
      toast.error(apiError(err, t('auth.register.failed')));
    } finally { setBusy(false); }
  };

  const perks = t('auth.register.perks', { returnObjects: true });

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-4 py-12 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8"><Wind className="w-7 h-7 text-[#00B4D8]" /><span className="font-display text-2xl">KITEIQ</span></Link>
          <h1 className="text-2xl font-semibold">{t('auth.register.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('auth.register.subtitle')}</p>
          {refCode && (
            <div className="mt-4 flex items-start gap-3 rounded-xl border border-[#2EE59D]/30 bg-[#2EE59D]/10 px-4 py-3" data-testid="register-referral-banner">
              <Gift className="w-5 h-5 text-[#2EE59D] shrink-0 mt-0.5" />
              <p className="text-sm text-[#2EE59D]">{t('auth.register.invitedBanner', { credits: 2 })}</p>
            </div>
          )}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">{t('auth.register.name')}</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t('auth.register.namePlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email">{t('auth.register.email')}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth.register.emailPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.register.password')}</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.register.passwordPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="register-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="register-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.register.submit')}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            {t('auth.register.haveAccount')} <Link to="/login" className="text-[#48CAE4] hover:text-[#00B4D8] font-medium">{t('auth.register.login')}</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={SIDE} alt="Kitesurfing" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.85))' }} />
        <div className="absolute bottom-10 left-10 right-10">
          <h2 className="font-display text-5xl">{t('auth.register.sideTitle1')}<br/><span className="text-gradient-blue">{t('auth.register.sideTitle2')}</span></h2>
          <ul className="mt-4 space-y-2">
            {(Array.isArray(perks) ? perks : []).map((p) => (
              <li key={p} className="flex items-center gap-2 text-white/80"><Check className="w-4 h-4 text-[#2EE59D]" /> {p}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
